/* ----------------------------------------------------------------------------------------------------
 * GIFT WRAPPING — <gift-wrapping> custom element
 *
 * Native replacement for the AVADA "Wrapped" app. Adds / removes the R150 "Gift Wrapping" product
 * (resolved server-side by handle, with a hard-coded variant fallback) via the Shopify AJAX Cart API,
 * and stores To / From / Message as line-item properties on that line (so they appear on the order and
 * packing slip — Impact's line-item.liquid already renders non-underscore properties).
 *
 * Refreshes the Impact cart drawer in place via the Cart API `sections` param, so we never touch the
 * minified theme.js bundle and never collapse the open drawer.
 * -------------------------------------------------------------------------------------------------- */

if (!customElements.get('gift-wrapping')) {
  class GiftWrapping extends HTMLElement {
    connectedCallback() {
      this.variantId = this.dataset.variantId;
      this.sectionId = this.dataset.sectionId || 'cart-drawer';
      this.context = this.dataset.context || 'cart';
      this.lineKey = this.dataset.lineKey || null;

      this.checkbox = this.querySelector('.gift-wrapping__checkbox');
      this.fields = this.querySelector('.gift-wrapping__fields');
      this.toInput = this.querySelector('[name="gift-to"]');
      this.fromInput = this.querySelector('[name="gift-from"]');
      this.messageInput = this.querySelector('[name="gift-message"]');
      this.counter = this.querySelector('.gift-wrapping__counter');
      this.status = this.querySelector('.gift-wrapping__status');

      this._onToggle = this._onToggle.bind(this);
      this._onInput = this._onInput.bind(this);

      if (this.checkbox) this.checkbox.addEventListener('change', this._onToggle);
      [this.toInput, this.fromInput, this.messageInput].forEach((el) => {
        if (el) el.addEventListener('input', this._onInput);
      });

      this._reflectActive();
      this._updateCounter();
    }

    /* ---- helpers ---- */

    get _properties() {
      return {
        To: this.toInput ? this.toInput.value : '',
        From: this.fromInput ? this.fromInput.value : '',
        Message: this.messageInput ? this.messageInput.value : '',
      };
    }

    _busy(state) {
      // Note: we intentionally do NOT disable the checkbox/fields here. Disabling the checkbox would
      // trigger Impact's ".checkbox:disabled ~ label" greying AND block the customer from typing their
      // message while the add/remove request is in flight. The checkbox is guarded against re-clicks
      // via pointer-events in CSS (.is-busy) instead; the message fields stay fully editable.
      this.classList.toggle('is-busy', state);
    }

    _reflectActive() {
      const on = !!(this.checkbox && this.checkbox.checked);
      this.classList.toggle('is-active', on);
      if (this.fields) this.fields.hidden = !on;
      if (this.status) this.status.hidden = !on;
    }

    _updateCounter() {
      if (this.counter && this.messageInput) {
        this.counter.textContent = `${this.messageInput.value.length} / ${this.messageInput.maxLength}`;
      }
    }

    /* ---- events ---- */

    _onToggle() {
      this._reflectActive();
      if (this.checkbox.checked) {
        this._add();
      } else {
        this._remove();
      }
    }

    _onInput() {
      this._updateCounter();
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this._saveProperties(), 500);
    }

    /* ---- cart operations ---- */

    async _add() {
      this._busy(true);
      try {
        const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(this.variantId), quantity: 1, properties: this._properties }],
            sections: [this.sectionId],
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || 'Add to cart failed');
        await this._syncLineKey();
        this._refresh(data.sections, false);
      } catch (err) {
        console.error('[gift-wrapping] add failed', err);
        if (this.checkbox) this.checkbox.checked = false;
        this._reflectActive();
      } finally {
        this._busy(false);
      }
    }

    async _remove() {
      if (!this.lineKey) await this._syncLineKey();
      if (!this.lineKey) {
        this._reflectActive();
        return;
      }
      this._busy(true);
      try {
        const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: this.lineKey,
            quantity: 0,
            sections: [this.sectionId],
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        this.lineKey = null;
        this._refresh(data.sections, false);
      } catch (err) {
        console.error('[gift-wrapping] remove failed', err);
      } finally {
        this._busy(false);
      }
    }

    async _saveProperties() {
      if (!this.checkbox || !this.checkbox.checked) return;
      if (!this.lineKey) await this._syncLineKey();
      if (!this.lineKey) return;
      try {
        const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: this.lineKey,
            properties: this._properties,
            sections: [this.sectionId],
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        this._applyCartData(data); // properties change rewrites the line key
        // keepFocus = true: do NOT re-render the drawer while the customer is typing
        this._refresh(data.sections, true);
      } catch (err) {
        console.error('[gift-wrapping] save properties failed', err);
      }
    }

    /* ---- state sync + UI refresh ---- */

    async _syncLineKey() {
      try {
        const res = await fetch(`${window.Shopify.routes.root}cart.js`);
        this._applyCartData(await res.json());
      } catch (err) {
        /* noop */
      }
    }

    _applyCartData(cart) {
      if (!cart || !cart.items) return;
      const item = cart.items.find((i) => String(i.variant_id) === String(this.variantId));
      this.lineKey = item ? item.key : null;
      if (typeof cart.item_count === 'number') {
        document.querySelectorAll('cart-count').forEach((el) => {
          el.textContent = cart.item_count;
        });
      }
    }

    async _refresh(sections, keepFocus) {
      // Always keep the header count in sync.
      if (!sections || !sections[this.sectionId]) {
        this._syncLineKey();
      }

      // Smoothly refresh the cart drawer's inner content (keeps the <cart-drawer> element + its open
      // state alive — we only swap innerHTML). Skipped while the customer is typing a message
      // (keepFocus) so we never steal focus mid-message.
      if (!keepFocus && sections && sections[this.sectionId]) {
        const drawer = document.getElementById('cart-drawer');
        if (drawer) {
          const doc = new DOMParser().parseFromString(sections[this.sectionId], 'text/html');
          const fresh = doc.getElementById('cart-drawer');
          if (fresh) drawer.innerHTML = fresh.innerHTML;
        }
      }

      // On the full cart page, reload so the new line + totals render (not while typing).
      const pageType = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType;
      if (!keepFocus && pageType === 'cart') {
        window.location.reload();
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }

  customElements.define('gift-wrapping', GiftWrapping);
}
